import { MONTH_TEXT_MAP, Direction, MANDATE_AMOUNT_ARR } from '../constants';
import { KYCStatusResponse } from '../models/KYCStatusResponse';
import { parse } from 'query-string';
// @ts-ignore
import { Router } from '../configs/routes';
import { VARIANT_KEY, VARIANT_VALUES } from '../constants/variantConstants';

export function getApiDateFormat(dateObj: Date) {
  return `${getOrdinalSuffix(dateObj.getDate())} ${
    MONTH_TEXT_MAP[dateObj.getMonth() + 1]
  }`;
}

export function redirectToLastRoute(mode?: string) {
  let lastRoute = decodeURIComponent(
    parse(location.search).refer || '/save'
  ).replace(/^(https?\:\/\/[a-z.-]+)\/?/g, '/save');
  lastRoute = lastRoute.replace(/(\?|\&)auth_provider=[a-z_]*/g, '');
  lastRoute += `${
    lastRoute.indexOf('?') !== -1 ? '&' : '?'
  }auth_provider=${mode}`;
  Router.push(lastRoute);
}
export const lazyLoadImages = (selector: string = 'img') => {
  function createObserver() {
    const elements = document.querySelectorAll(selector);
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[], observerChild) => {
        entries.forEach((entry: any) => {
          if (entry.isIntersecting && entry.target.getAttribute('data-src')) {
            entry.target.src = entry.target.getAttribute('data-src');
            entry.target.removeAttribute('data-src');
            observerChild.unobserve(entry.target);
          }
        });
      },
      {}
    );

    Array.prototype.map.call(elements, (item: any) => {
      observer.observe(item);
    });
  }

  if (!('IntersectionObserver' in window)) {
    const polyfill = document.createElement('script');
    polyfill.src =
      'https://cdn.jsdelivr.net/npm/intersection-observer@0.7.0/intersection-observer.js';
    document.head.appendChild(polyfill);

    polyfill.onload = () => {
      createObserver();
    };
    return;
  }

  createObserver();
};

export function splitInvestments(
  amount: number,
  products: any[],
  investmentType: string
) {
  let finalAmount = amount;

  const finalProducts = products.map((product: any) => {
    let minAmt = 0;
    if (investmentType === Direction.SIP && product.minSipAmount >= 500) {
      finalAmount -= product.minSipAmount;
      minAmt = product.minSipAmount;
    } else if (product.minAmount >= 500) {
      finalAmount -= product.minAmount;
      minAmt = product.minAmount;
    }
    return { ...product, amount: minAmt };
  });

  while (finalAmount > 0) {
    for (let i = 0; i < products.length; i += 1) {
      finalProducts[i].amount += 500;
      finalAmount -= 500;
      if (finalAmount <= 0) {
        break;
      }
    }
  }
  return finalProducts;
}

export function roundOffTo500(money: number) {
  const roundoff = Math.ceil(money / 500) * 500;
  return roundoff || money;
}

export function roundOffToStep(money: number, step: number) {
  const roundoff = Math.ceil(money / step) * step;
  return roundoff || money;
}

export function getOrdinalSuffix(i: number) {
  const j = i % 10;
  const k = i % 100;
  if (j === 1 && k !== 11) {
    return `${i}st`;
  }
  if (j === 2 && k !== 12) {
    return `${i}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${i}rd`;
  }
  return `${i}th`;
}

export function numberToCurrency(num: number | string) {
  let receivedNum = num || '';
  // make sure input is of type string
  receivedNum = receivedNum.toString();
  let lastThree = receivedNum.substring(receivedNum.length - 3);
  const otherNumbers = receivedNum.substring(0, receivedNum.length - 3);
  if (otherNumbers !== '') {
    lastThree = `,${lastThree}`;
  }
  const formattedNumber =
    otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
  return formattedNumber;
}

export function kycNotVerfied(kycStatusResponse: KYCStatusResponse) {
  const { allowedToProcessOrder = true } = kycStatusResponse || {};
  return typeof allowedToProcessOrder === 'boolean' && !allowedToProcessOrder;
}

export function getAmountSelectData(minAmount: number) {
  // return the amount if greater than Rs. 20000 /-
  if (minAmount >= 20000) {
    return [{ label: minAmount, value: minAmount }];
  }

  return MANDATE_AMOUNT_ARR.filter((amount: number) => amount > minAmount).map(
    (amount: number) => {
      return {
        label: amount,
        value: amount,
      };
    }
  );
}

export const getFormattedDate = (
  timestamp: number | string,
  getFullMonthName: boolean = false
) => {
  if (!timestamp) {
    return '--';
  }
  const date = new Date(timestamp);
  const day = date.getDate();
  const month = getFullMonthName
    ? MONTH_TEXT_MAP[date.getMonth() + 1]
    : MONTH_TEXT_MAP[date.getMonth() + 1].slice(0, 3);
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const formatRupee = (
  rupee: number | string,
  decimal: boolean = true
) => {
  rupee = rupee || 0;
  const options = decimal
    ? {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    : {};
  const r = Number(Number(rupee)).toLocaleString('en-IN', options);
  return r;
};

export const getSuggestedAmountSafely = (
  response: any,
  isSIP: boolean,
  sipKey: string,
  lumpSumKey: string,
  hasUserInvestedInProduct: boolean,
  suggestedAmount?: number
) => {
  const minSipAmount = response[sipKey];
  const minLumpSumAmount =
    hasUserInvestedInProduct && response['minSubsequentAmount']
      ? response['minSubsequentAmount']
      : response[lumpSumKey];
  let suggestedSipAmount = response.suggestedSipAmount;
  let suggestedLumpSumAmount = response.suggestedLumpSumAmount;
  suggestedSipAmount = suggestedSipAmount || minSipAmount;
  suggestedLumpSumAmount = suggestedLumpSumAmount || minLumpSumAmount;
  return suggestedAmount
    ? suggestedAmount
    : isSIP
    ? suggestedSipAmount
    : suggestedLumpSumAmount;
};

export const scrollToTarget = (target: string, ele: any = null) => {
  const el = document.querySelector(`.${target}`);
  const position =
    Math.max(document.documentElement.scrollTop, document.body.scrollTop) +
    (el && el.getBoundingClientRect().top) -
    (ele ? ele.getBoundingClientRect().height : 40) -
    69;
  window.scrollTo({
    top: position,
    behavior: 'smooth',
  });
};

export const scrollToTargetById = (
  scrollToID: string,
  eleOffset: number = 110
) => {
  const ele = document.getElementById(scrollToID);
  if (ele) {
    const eleTop = ele.offsetTop - eleOffset;
    window.scrollTo({
      top: eleTop,
      behavior: 'smooth',
    });
  }
};

export const getVariant = (url: any) => {
  return url.query[VARIANT_KEY] || VARIANT_VALUES.A;
};

export const isMobileDevice = (
  userAgent = navigator.userAgent || navigator.vendor || window.opera
) => {
  let check = false;
  ((a) => {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    ) {
      check = true;
    }
  })(userAgent);
  return check;
};

export const isSmallScreen = () => {
  return window.innerWidth <= 767;
};

export const getFullName = (fName: string, mName: string, lName: string) => {
  return `${fName || ''} ${mName || ''} ${lName || ''}`
    .replace(/\s+/g, ' ')
    .trim();
};

export const getEventCategoryPrifix = (url: any) => {
  let { pathname = '' } = url || {};
  pathname = pathname && pathname.slice(1);
  const pathnameArr = ['personalized-plan'];
  if (pathnameArr.includes(pathname) === true) {
    return `${pathname}-`;
  } else {
    return '';
  }
};

export const debounce = (func: any, delay: number) => {
  let timeout;
  return function debouncedFn() {
    const functionCall = () => func.apply(this, arguments);
    clearTimeout(timeout);
    timeout = setTimeout(functionCall, delay);
  };
};

export const getUnitAllocationDate = (LiquidFunds: boolean = false): string => {
  const automationRunHours = LiquidFunds ? 13.5 : 15;
  const date = new Date();
  const day = date.getDay();
  const hours = date.getHours() + date.getMinutes() / 60;
  if (day > 5 || day === 0 || (day === 5 && hours >= automationRunHours)) {
    date.setDate(date.getDate() + ((1 + 7 - date.getDay()) % 7));
  } else if (hours >= automationRunHours) {
    date.setDate(date.getDate() + 1);
  }
  return getFormattedDate(date.getTime());
};

export const checkEmail = (email: string) => {
  const regEx =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // Check for valid email format
  return regEx.test(String(email).toLowerCase());
};

export const parseFundRating = (rating: string) => {
  const ratings = rating && rating.split('/');
  const fundRating = ratings && parseInt(ratings[0], 10);
  const maxFundRating = ratings && parseInt(ratings[1], 10);
  return {
    fundRating,
    maxFundRating,
  };
};

// Function to add zero before if time < 10
export const normalizeTime = (time: number) => {
  return time < 10 ? `0${time}` : `${time}`;
};

// Function to calculate difference between startDate and endDate
export const getRemainingTime = (startDate: Date, endDate: Date) => {
  const second = 1000;
  const minute = second * 60;
  const hour = minute * 60;
  const day = hour * 24;

  const distance = Math.abs(endDate.getTime() - startDate.getTime());

  return {
    inDays: normalizeTime(Math.floor(distance / day)),
    inHours: normalizeTime(Math.floor((distance % day) / hour)),
    inMinutes: normalizeTime(Math.floor((distance % hour) / minute)),
    inSeconds: normalizeTime(Math.floor((distance % minute) / second)),
  };
};

export const randomNumber = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min) + min);
};

export function truncate(str: string, length: number) {
  if (length > str.length) {
    return str;
  }
  return str.toString().slice(0, length) + '...';
}

// Return the number after two decimal places without rounding
export const fixedByDecimal = (num: number, decimalCount: number = 2) => {
  try {
    if (num) {
      const strArr = String(num).split('.');
      let updateNumStr = strArr[0];
      if (strArr[1] && strArr[1].length > 0) {
        updateNumStr = updateNumStr + '.' + strArr[1].slice(0, decimalCount);
      }
      return updateNumStr;
    } else {
      return num;
    }
  } catch (err) {
    return num;
  }
};

export const formatRoundedRupeeLiteral = (value: number) => {
  if (!value) {
    return 0;
  }
  let temp;
  if (value > 10000000) {
    temp = `${parseFloat((value / 10000000).toFixed(0)).toLocaleString(
      'en-IN'
    )} Cr`;
  } else if (value > 100000) {
    temp = `${parseFloat((value / 100000).toFixed(0)).toLocaleString(
      'en-IN'
    )} L`;
  } else {
    temp = `${parseInt(value.toString(), 10).toLocaleString('en-IN')}`;
  }
  return temp;
};

export function formatEpochToDateString(epochMilliseconds: string | number) {
  const date = new Date(Number(epochMilliseconds));
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getMonth()];
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
}

export function calculateAge(epochMilliseconds: string | number) {
  const dob = new Date(Number(epochMilliseconds));
  const timeDiff = Date.now() - dob.getTime();
  const ageDate = new Date(timeDiff);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
}



// WEBPACK FOOTER //
// ./utils/commonUtils.ts